import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { DEFAULT_LIMITS, type SimContext, WATCHDOG_GRACE_MS } from "../core/context";
import {
  type ErrorMessage,
  parseWorkerMessage,
  type ReadyMessage,
  type UiMessage,
} from "../core/protocol";
import { initialTimeline, type TimelineState, timelineReducer } from "../core/timeline";

declare const AFTERIMAGE_WORKER_URL: string;

/** Injected by scripts/build.ts; the dev server serves the worker at a stable path. */
const workerUrl = typeof AFTERIMAGE_WORKER_URL === "string" ? AFTERIMAGE_WORKER_URL : "/worker.js";

export type EngineStatus = { state: "booting" } | ReadyMessage | ErrorMessage;

export interface Runner {
  /** Boot status of the pre-warmed worker. */
  engine: EngineStatus;
  timeline: TimelineState;
  run(code: string, context: SimContext, seed: number): void;
  stop(): void;
}

/**
 * Owns the worker lifecycle (ADR-0004): one pre-warmed worker waits for the
 * next run; starting a run hands it the code and spawns the next one. Stop
 * and the watchdog terminate the running worker.
 */
export function useRunner(): Runner {
  const [engine, setEngine] = useState<EngineStatus>({ state: "booting" });
  const [timeline, dispatch] = useReducer(timelineReducer, initialTimeline);
  const spare = useRef<Worker | null>(null);
  const active = useRef<{ worker: Worker; watchdog: ReturnType<typeof setTimeout> } | null>(null);
  const runCount = useRef(0);

  const finishActive = useCallback(() => {
    if (!active.current) return;
    clearTimeout(active.current.watchdog);
    active.current.worker.terminate();
    active.current = null;
  }, []);

  const spawn = useCallback(() => {
    const worker = new Worker(workerUrl, { type: "module" });
    worker.onmessage = (event) => {
      const message = parseWorkerMessage(event.data);
      if (!message) return;
      if (worker === spare.current && (message.type === "ready" || message.type === "error")) {
        setEngine(message);
      }
      if (worker !== active.current?.worker) return;
      dispatch({ type: "message", message });
      if (message.type === "done" || message.type === "error") finishActive();
    };
    worker.onerror = (event) => {
      const message = { type: "error" as const, message: event.message || "Worker failed" };
      if (worker === spare.current) setEngine(message);
      if (worker === active.current?.worker) {
        dispatch({ type: "message", message });
        finishActive();
      }
    };
    spare.current = worker;
  }, [finishActive]);

  useEffect(() => {
    spawn();
    return () => {
      spare.current?.terminate();
      spare.current = null;
      finishActive();
    };
  }, [spawn, finishActive]);

  const run = useCallback(
    (code: string, context: SimContext, seed: number) => {
      const worker = spare.current;
      if (!worker || active.current) return;
      const runId = `run-${++runCount.current}`;
      const watchdog = setTimeout(() => {
        dispatch({
          type: "message",
          message: { type: "error", runId, message: "The run stopped responding and was killed" },
        });
        finishActive();
      }, DEFAULT_LIMITS.cpuMs + WATCHDOG_GRACE_MS);
      active.current = { worker, watchdog };
      dispatch({ type: "start", runId });
      const message: UiMessage = {
        type: "run",
        runId,
        code,
        mode: "script",
        context,
        seed,
        environment: "browser",
      };
      worker.postMessage(message);
      setEngine({ state: "booting" });
      spawn();
    },
    [spawn, finishActive],
  );

  const stop = useCallback(() => {
    if (!active.current) return;
    finishActive();
    dispatch({ type: "cancel" });
  }, [finishActive]);

  return { engine, timeline, run, stop };
}
